/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion } from "framer-motion";
import { FaApple, FaFacebook, FaGooglePlay, FaInstagram, FaWhatsapp, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { FiClock, FiMail, FiPhone } from "react-icons/fi";
import { useContactData } from "~/hook/useContactData";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";
import ClientOnly from "~/components/shared-components/ClentOnly";

const SocialMediaSection = () => {
  const { medium, normalInter } = useFontStyle();
  const contactData = useContactData();

  const { socialMedia, quickContact } = contactData;

  const getIcon = (iconType: string, iconUrl?: string) => {
    if (iconType === "custom" && iconUrl) {
      return <img src={iconUrl} alt="Channel Icon" className="w-10 h-10 rounded-full object-cover select-none" />;
    }

    const iconMap = {
      facebook: <FaFacebook size={22} color="#1877F2" />,
      instagram: <FaInstagram size={22} color="#E4405F" />,
      twitter: <FaXTwitter size={22} color="#000000" />,
      whatsapp: <FaWhatsapp size={22} color="#25D366" />,
      youtube: <FaYoutube size={22} color="#FF0000" />,
      apple: <FaApple size={22} color="#000000" />,
      googleplay: <FaGooglePlay size={22} color="#3DDC84" />,
    };

    return iconMap[iconType as keyof typeof iconMap] || null;
  };

  const renderIconSection = (title: string, links: any[], delay: number) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay }} className="mb-6">
      <h4 className={cn("body_text_l text-textHeadingColor mb-3 flex items-center", medium)}>
        <div className="w-3 h-3 bg-textHeadingColor rounded-full mr-3"></div>
        {title}
      </h4>
      <div className="flex flex-wrap gap-2 ">
        {links.map((link, index) => (
          <motion.a
            key={link.name}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: delay + index * 0.1 }}
            whileHover={{ scale: 1.05, y: -1 }}
            whileTap={{ scale: 0.95 }}
            className={cn(
              "group relative flex items-center justify-center w-14 h-14 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md",
              link.bgColor,
              link.hoverBgColor,
            )}
            title={link.name}
          >
            <div className={cn("transition-transform duration-300 group-hover:scale-110", link.iconColor)}>
              {getIcon(link.iconType, link.iconUrl)}
            </div>

            {/* Tooltip */}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-10">
              {link.name}
            </div>
          </motion.a>
        ))}
      </div>
    </motion.div>
  );

  const renderIconSectionStatic = (title: string, links: any[]) => (
    <div className="mb-6">
      <h4 className={cn("body_text_l text-textHeadingColor mb-3 flex items-center", medium)}>
        <div className="w-3 h-3 bg-textHeadingColor rounded-full mr-3"></div>
        {title}
      </h4>
      <div className="flex flex-wrap gap-2 ">
        {links.map((link) => (
          <a
            key={link.name}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "group relative flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md",
              link.bgColor,
              link.hoverBgColor,
            )}
            title={link.name}
          >
            <div className={cn("transition-transform duration-300 group-hover:scale-110", link.iconColor)}>
              {getIcon(link.iconType, link.iconUrl)}
            </div>

            {/* Tooltip */}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-10">
              {link.name}
            </div>
          </a>
        ))}
      </div>
    </div>
  );

  return (
    <ClientOnly
      fallback={
        <div className="space-y-6 ">
          {/* Header - Static version without motion */}
          <div className="text-center lg:text-left">
            <h3 className={cn("h4_heading text-textHeadingColor mb-3", medium)}>{socialMedia.title}</h3>
            <p className={cn("text-textColorPrimary body_text_m", normalInter)}>{socialMedia.description}</p>
          </div>

          {/* Social Media Icons */}
          {renderIconSectionStatic("Social Media", socialMedia.platforms)}

          {/* YouTube Channels */}
          {renderIconSectionStatic("YouTube Channels", socialMedia.youtubeChannels)}

          {/* Mobile Apps */}
          {renderIconSectionStatic("Mobile Apps", socialMedia.mobileApps)}
        </div>
      }
    >
      <div className="space-y-6 ">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center lg:text-left"
        >
          <h3 className={cn("h4_heading text-textHeadingColor mb-3", medium)}>{socialMedia.title}</h3>
          <p className={cn("text-textColorPrimary body_text_m", normalInter)}>{socialMedia.description}</p>
        </motion.div>

        {/* Social Media Icons */}
        {renderIconSection("Social Media", socialMedia.platforms, 0.4)}

        {/* YouTube Channels */}
        {renderIconSection("YouTube Channels", socialMedia.youtubeChannels, 0.5)}

        {/* Mobile Apps */}
        {renderIconSection("Mobile Apps", socialMedia.mobileApps, 0.6)}
      </div>
    </ClientOnly>
  );
};

export default SocialMediaSection;
